import React, { useMemo } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import GenericTableFilter from '../../../../../components/common/GenericTableFilter';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useGetDealerDropdownListQuery } from '../../../../../api/endpoints/dealer';
import DealerDropdown from '../../../../../components/common/DealerDropdownWithMulti';
import CountryDropdownWithMultiSelect from '../../../../../components/common/CountryDropdownWithMultiSelect';
import ChassisMultiSelect from '../../../../../components/common/ChassisDropdownWithMultiSelect';
import CustomerDropdown from '../../../../../components/common/CustomerDropdown';

const CustomerDeclarationFilter = ({ handleApplyFilters, handleResetFilters }) => {
    const { t } = useLanguage();

    const { data: dealerList = [] } = useGetDealerDropdownListQuery({
        RowCount: 100,
        SearchText: ''
    });

    const dealerOptions = useMemo(() => {
        return dealerList.map((dealer) => ({
            label: dealer.name,
            value: dealer.dealerId
        }));
    }, [dealerList]);

    const statusOptions = useMemo(() => [
        { label: t('warranty.customerDeclaration.pdf.statusOpen'), value: 5 },
        { label: t('warranty.customerDeclaration.pdf.statusClose'), value: 10 }
    ], [t]);

    const invoiceStatusOptions = useMemo(() => [
        { label: t('warranty.customerDeclaration.filters.invoiced'), value: true },
        { label: t('warranty.customerDeclaration.filters.notInvoiced'), value: false }
    ], [t]);

    const filterFields = useMemo(
        () => [
            {
                field: 'id',
                headerName: t('warranty.customerDeclaration.table.workOrderNo'),
                type: 'custom',
                customComponent: ({ value, onChange }) => (
                    <InputNumber
                        value={value === '' ? null : value}
                        onValueChange={(e) => onChange(e.value)}
                        useGrouping={false}
                        placeholder={t('filter.inputPlaceholder', { field: t('warranty.customerDeclaration.table.workOrderNo') })}
                        className="w-full"
                        inputClassName="w-full"
                    />
                )
            },
            {
                field: 'startDate',
                headerName: t('common.startDate'),
                type: 'date'
            },
            {
                field: 'endDate',
                headerName: t('common.endDate'),
                type: 'date'
            },
            {
                field: 'dealerId',
                headerName: t('warranty.customerDeclaration.table.dealer'),
                type: 'custom',
                customComponent: ({ value, onChange }) => (
                    <DealerDropdown
                        value={value}
                        onChange={onChange}
                        className="w-full"
                        multiple={true}
                        showSelectAll={true}
                    />
                )
            },
            {
                field: 'country',
                headerName: t('warranty.customerDeclaration.table.country'),
                type: 'custom',
                customComponent: ({ value, onChange }) => (
                    <CountryDropdownWithMultiSelect
                        value={value}
                        onChange={onChange}
                        className="w-full"
                        showSelectAll={true}
                    />
                )
            },
            {
                field: 'status',
                headerName: t('common.status'),
                type: 'dropdown',
                options: statusOptions
            },
            {
                field: 'isInvoiced',
                headerName: t('warranty.customerDeclaration.filters.invoiceStatus'),
                type: 'dropdown',
                options: invoiceStatusOptions
            },
            {
                field: 'chassisNo',
                headerName: t('warranty.customerDeclaration.table.chassisNo'),
                type: 'custom',
                customComponent: ({ value, onChange }) => (
                    <ChassisMultiSelect
                        value={value}
                        onChange={onChange}
                        className="w-full"
                        showDescription={false}
                    />
                )
            },
            {
                field: 'licensePlate',
                headerName: t('warranty.customerDeclaration.table.licensePlate'),
                type: 'text'
            },
            {
                field: 'customerId',
                headerName: t('warranty.customerDeclaration.table.customer'),
                type: 'custom',
                customComponent: ({ value, onChange }) => (
                    <CustomerDropdown
                        value={value}
                        onChange={(id, data) => {
                            onChange(id);
                        }}
                        className="w-full"
                        placeholder={t('fullServiceInvoice.placeholders.customer')}
                    />
                )
            }
        ],
        [t, dealerOptions, statusOptions, invoiceStatusOptions]
    );

    const customFilterTypes = useMemo(
        () => ({
            id: 'number',
            dealerId: 'custom',
            country: 'custom',
            chassisNo: 'custom',
            licensePlate: 'text',
            customerId: 'custom',
            startDate: 'date',
            endDate: 'date',
            status: 'dropdown',
            isInvoiced: 'dropdown'
        }),
        []
    );

    return (
        <div className="mb-2">
            <GenericTableFilter
                filterFields={filterFields}
                customFilterTypes={customFilterTypes}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
            />
        </div>
    );
};

export default CustomerDeclarationFilter;
